import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { createTask, updateTask, getAllSprints, createSprint } from "../../services/Service";
import { useGetProjectsQuery } from "../../services/rtkClient";
import { useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import "./CreateTask.css";
import { Calendar as CalendarIcon, Lock, Maximize2, MoreHorizontal, X } from 'lucide-react';

const CreateTask = ({ onClose, onAddTask, initialColumn, initialData, currentProjectId }) => {
    const location = useLocation();
    const getCurrentUser = () => {
        try {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                return JSON.parse(storedUser);
            }
        } catch (err) { }
        return null;
    };
    const currentUserInfo = getCurrentUser();
    const currentUserName = currentUserInfo ? (currentUserInfo.firstName ? `${currentUserInfo.firstName} ${currentUserInfo.lastName || ''}`.trim() : (currentUserInfo.username || currentUserInfo.email || "Current User")) : "Current User";
    const currentUserId = currentUserInfo?.id || currentUserInfo?.userId || currentUserInfo?._id || 1;
    const { data: projectsResponse } = useGetProjectsQuery();
    const projects = React.useMemo(() => {
        // Handle result of transformResponse (which is already mapped)
        if (Array.isArray(projectsResponse) && projectsResponse.length > 0) {
            return [...projectsResponse].map(p => ({
                ...p,
                id: p.id || p.projectId,
                name: p.name || p.projectName || "Unnamed Project"
            })).reverse();
        }
        // Fallback if no projects from API
        return [];
    }, [projectsResponse]);

    const fallbackProjectId = location.state?.projectId;
    const fallbackProjectName = location.state?.projectName;

    const [projectId, setProjectId] = useState(() => {
        if (initialData?.projectId) return Number(initialData.projectId);
        if (currentProjectId) return Number(currentProjectId);
        if (fallbackProjectId) return Number(fallbackProjectId);
        const sessionProjectId = sessionStorage.getItem('projectId');
        if (sessionProjectId) return Number(sessionProjectId);
        // Default to "" to avoid React warning about null value on select
        return projects.length > 0 ? Number(projects[0].id) : "";
    });

    // Re-sync projectId when currentProjectId prop or projects resolve from API
    useEffect(() => {
        if (currentProjectId && !initialData?.projectId) {
            setProjectId(Number(currentProjectId));
        } else if (!projectId && projects.length > 0) {
            setProjectId(Number(projects[0].id || 1));
        }
    }, [currentProjectId, projects, projectId, initialData?.projectId]);

    // Ensure fallback project is in the list
    const projectsWithFallback = React.useMemo(() => {
        const list = [...projects];
        if (fallbackProjectId && !list.find(p => Number(p.id) === Number(fallbackProjectId))) {
            list.unshift({ id: fallbackProjectId, name: fallbackProjectName || "Recent Project" });
        }
        return list;
    }, [projects, fallbackProjectId, fallbackProjectName]);

    const [issueKey, setIssueKey] = useState(initialData?.issueKey || '');
    const [summary, setSummary] = useState(initialData?.summary || (initialData?.title) || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [issueType, setIssueType] = useState(initialData?.issueType || 'task');
    const [status, setStatus] = useState(initialData?.status || initialColumn || 'todo');
    const [priority, setPriority] = useState(initialData?.priority || 'medium');
    const [reporterId, setReporterId] = useState(Number(initialData?.reporterId) || currentUserId);
    const [assigneeId, setAssigneeId] = useState(Number(initialData?.assigneeId) || 1);
    const [sprintId, setSprintId] = useState(Number(initialData?.sprintId) || 1);
    const [estDays, setEstDays] = useState(Math.floor((Number(initialData?.originalEstimate) || 0) / 8));
    const [estHours, setEstHours] = useState((Number(initialData?.originalEstimate) || 0) % 8);
    const [orderIndex, setOrderIndex] = useState(Number(initialData?.orderIndex) || 1);
    const [startDate, setStartDate] = useState(initialData?.startDate || new Date().toISOString());
    const [dueDate, setDueDate] = useState(initialData?.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString());
    const [storyPoints] = useState(Number(initialData?.storyPoints) || 1);
    const [epicId] = useState(Number(initialData?.epicId) || 1);
    const [parentIssueId] = useState(Number(initialData?.parentIssueId) || 1);
    const [createAnother, setCreateAnother] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sprints, setSprints] = useState([]);
    const [newSprintName, setNewSprintName] = useState("");
    const [isCreatingSprint, setIsCreatingSprint] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSprints = async () => {
            try {
                const result = await getAllSprints();
                // Assuming result.data.data contains the array of sprints based on cryptoBaseQuery decryption
                const sprintList = result?.data || result?.schema || (Array.isArray(result) ? result : []);
                setSprints(sprintList);
                if (sprintList.length > 0 && !initialData?.sprintId) {
                    setSprintId(Number(sprintList[0].sprintId || sprintList[0].id));
                }
            } catch (err) {
                console.error("Failed to fetch sprints:", err);
            }
        };
        fetchSprints();
    }, [initialData?.sprintId]);

    const handleCreateSprint = async () => {
        if (!newSprintName.trim()) return;
        setIsCreatingSprint(true);
        try {
            const sprintData = {
                projectId: Number(projectId),
                sprintName: newSprintName.trim(),
                startDate: new Date().toISOString(),
                endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // Default 2 weeks
                status: "planned"
            };
            const result = await createSprint(sprintData);
            const newSprint = result?.data || result?.schema || result;
            setSprints([...sprints, newSprint]);
            setSprintId(Number(newSprint.sprintId || newSprint.id));
            setNewSprintName("");
        } catch (err) {
            setError("Failed to create sprint. Please try again.");
        } finally {
            setIsCreatingSprint(false);
        }
    };

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', { 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image', 'video'],
            ['clean']
        ],
    };

    const formats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'color', 'background', 'list', 'indent',
        'align', 'link', 'image', 'video'
    ];

    return (
        <div className="create-task-overlay">
            <div className="create-task-modal">
                <div className="create-task-header">
                    <h2>Create Task</h2>
                    <div className="header-actions">
                        <button className="icon-btn" title="Full screen"><Maximize2 size={18} /></button>
                        <button className="icon-btn" title="More actions"><MoreHorizontal size={18} /></button>
                        <button className="icon-btn" onClick={onClose} title="Close"><X size={18} /></button>
                    </div>
                </div>

                <div className="create-task-content">
                    {/* Row 1: Summary, Issue Key, Issue Type */}
                    <div className="form-row">
                        <div className="form-group flex-2">
                            <label className="form-label">Summary <span className="required">*</span></label>
                            <input
                                type="text"
                                className="form-input"
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                                placeholder=""
                            />
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label">Issue Key</label>
                            <input
                                type="text"
                                className="form-input"
                                value={issueKey}
                                onChange={(e) => setIssueKey(e.target.value)}
                                placeholder=""
                            />
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label">Issue Type</label>
                            <select
                                className="form-select"
                                value={issueType}
                                onChange={(e) => setIssueType(e.target.value)}
                            >
                                <option value="task">Task</option>
                                <option value="bug">Bug</option>
                                <option value="story">Story</option>
                                <option value="epic">Epic</option>
                            </select>
                        </div>
                    </div>

                    {/* Row 2: Project, Status, Priority */}
                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label className="form-label">Project <span className="required">*</span></label>
                            <select className="form-select" value={projectId || ""} onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : "")}>
                                {projectsWithFallback.map(proj => (
                                    <option key={proj.id} value={proj.id}>{proj.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label">Status</label>
                            <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="todo">To Do</option>
                                <option value="inprogress">In Progress</option>
                                <option value="done">Done</option>
                            </select>
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label">Priority</label>
                            <select className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                                <option value="lowest">Lowest</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="highest">Highest</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description</label>
                        <div className="quill-container">
                            <ReactQuill
                                theme="snow"
                                value={description}
                                onChange={setDescription}
                                modules={modules}
                                formats={formats}
                                placeholder="Type task description..."
                            />
                        </div>
                    </div>

                    {/* Meta Section 1: Reporter, Assignee, Sprint, Epic Link */}
                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label className="form-label">Reporter</label>
                            <input
                                type="text"
                                className="form-input"
                                value={currentUserName}
                                disabled
                                style={{ backgroundColor: '#f4f5f7', color: '#5e6c84', cursor: 'not-allowed' }}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label className="form-label">Assignee</label>
                            <select className="form-select" value={assigneeId} onChange={(e) => setAssigneeId(Number(e.target.value))}>
                                <option value={1}>Manas sir</option>
                                <option value={2}>Prasanta sir</option>
                            </select>
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label">Sprint</label>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <select className="form-select" value={sprintId} onChange={(e) => setSprintId(Number(e.target.value))}>
                                    {sprints.map(s => (
                                        <option key={s.sprintId || s.id} value={s.sprintId || s.id}>{s.sprintName || s.name}</option>
                                    ))}
                                    {sprints.length === 0 && <option value={1}>No Sprints</option>}
                                </select>
                            </div>
                            <div style={{ display: 'flex', marginTop: '5px', gap: '5px' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                    placeholder="New sprint..."
                                    value={newSprintName}
                                    onChange={(e) => setNewSprintName(e.target.value)}
                                />
                                <button
                                    className="btn btn-primary"
                                    style={{ fontSize: '10px', padding: '0 8px', height: '30px' }}
                                    onClick={handleCreateSprint}
                                    disabled={isCreatingSprint || !newSprintName.trim()}
                                >
                                    {isCreatingSprint ? '...' : 'Add'}
                                </button>
                            </div>
                        </div>
                        <div className="form-group flex-2"></div>
                    </div>

                    {/* Meta Section 2: Original Estimate */}
                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label className="form-label">Original Estimate</label>
                            <div className="dual-select-container">
                                <div className="select-with-label">
                                    <select className="form-select" value={estDays} onChange={(e) => setEstDays(Number(e.target.value))}>
                                        {[...Array(31)].map((_, i) => <option key={i} value={i}>{i}d</option>)}
                                    </select>
                                </div>
                                <div className="select-with-label">
                                    <select className="form-select" value={estHours} onChange={(e) => setEstHours(Number(e.target.value))}>
                                        {[...Array(8)].map((_, i) => <option key={i} value={i}>{i}h</option>)}
                                    </select>
                                </div>
                                <div className="estimate-preview">
                                    = {estDays * 8 + estHours}h
                                </div>
                            </div>
                        </div>
                        <div className="flex-3"></div>
                    </div>

                    {/* Dates Section */}
                    <div className="form-row" style={{ marginTop: '10px' }}>
                        <div className="form-group flex-1">
                            <label className="form-label" style={{ display: 'flex', alignItems: 'center' }}>
                                <Lock size={10} style={{ marginRight: '6px', color: '#6B778C' }} /> Start date
                            </label>
                            <div className="input-icon-wrapper">
                                <span className="input-icon"><CalendarIcon size={16} /></span>
                                <input
                                    type="date"
                                    className="form-input input-with-icon date-input-custom"
                                    value={startDate ? startDate.substring(0, 10) : ''}
                                    onChange={(e) => setStartDate(`${e.target.value}T11:07:03.780Z`)}
                                />
                            </div>
                        </div>
                        <div className="form-group flex-1">
                            <label className="form-label" style={{ display: 'flex', alignItems: 'center', height: '14px' }}>
                                Due date
                            </label>
                            <div className="input-icon-wrapper">
                                <span className="input-icon"><CalendarIcon size={16} /></span>
                                <input
                                    type="date"
                                    className="form-input input-with-icon date-input-custom"
                                    value={dueDate ? dueDate.substring(0, 10) : ''}
                                    onChange={(e) => setDueDate(`${e.target.value}T11:07:03.780Z`)}
                                />
                            </div>
                        </div>
                        <div className="flex-2"></div>
                    </div>

                    {error && <div className="error-message" style={{ color: 'red', padding: '10px', fontSize: '14px' }}>{error}</div>}
                </div>

                <div className="create-task-footer">
                    <div className="footer-left" onClick={() => setCreateAnother(!createAnother)}>
                        <div className={`custom-checkbox ${createAnother ? 'checked' : ''}`}>
                            {createAnother && <svg width="10" height="10" viewBox="0 0 24 24"><path d="M4.5,10L10,15.5L20,5" fill="none" stroke="white" strokeWidth="4" /></svg>}
                        </div>
                        Create another
                    </div>
                    <button className="btn btn-link" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" disabled={isLoading} onClick={async () => {
                        if (!summary.trim()) {
                            setError('Summary is required.');
                            return;
                        }
                        setIsLoading(true);
                        setError(null);
                        try {
                            const isEditing = !!initialData?.id || !!initialData?.issueId;
                            const resolvedProjectId = Number(projectId) || Number(currentProjectId) || Number(sessionStorage.getItem('projectId'));
                            if (!resolvedProjectId) {
                                setError('Please select a project before creating a task.');
                                setIsLoading(false);
                                return;
                            }
                            const isDone = (status || "").toLowerCase() === "done" || (status || "").toLowerCase() === "completed";
                            const generatedIssueKey = issueKey ? String(issueKey) : `KAN-${Math.floor(10000 + Math.random() * 90000)}`;
                            const taskData = {
                                projectId: resolvedProjectId,
                                summary,
                                issueKey: generatedIssueKey,
                                description,
                                issueType,
                                status,
                                priority,
                                reporterId: Number(reporterId),
                                assigneeId: Number(assigneeId),
                                sprintId: Number(sprintId),
                                originalEstimate: Number(estDays * 8 + estHours),
                                remainingEstimate: 0,
                                timeSpent: 0,
                                orderIndex: Number(orderIndex),
                                startDate,
                                dueDate,
                                epicId: Number(epicId),
                                parentIssueId: Number(parentIssueId),
                                storyPoints: Number(storyPoints),
                                resolution: isDone ? "resolved" : "unresolved",
                                resolvedAt: isDone ? new Date().toISOString() : "9999-12-31T23:59:59.999Z",
                                labels: {},
                                customFields: {},
                                deletedAt: "9999-12-31T23:59:59.999Z"
                            };

                            if (isEditing) {
                                taskData.issueId = Number(initialData.id || initialData.issueId);
                            }

                            console.log(isEditing ? "Updating Task:" : "Creating Task:", taskData);
                            const result = isEditing ? await updateTask(taskData) : await createTask(taskData);
                            if (onAddTask) onAddTask(result);

                            if (!createAnother) onClose();
                            else {
                                if (isEditing) onClose();
                                else {
                                    setSummary('');
                                    setDescription('');
                                    setIssueKey('');
                                    setIssueType('task');
                                    setStatus(initialColumn || 'todo');
                                    setPriority('medium');
                                    setReporterId(currentUserId);
                                    setAssigneeId(1);
                                    setSprintId(sprints[0]?.sprintId || sprints[0]?.id || 1);
                                    setEstDays(0);
                                    setEstHours(0);
                                    setOrderIndex(orderIndex + 1);
                                    setStartDate(new Date().toISOString());
                                    setDueDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString());
                                }
                            }
                        } catch (err) {
                            setError(`Failed to ${initialData?.id ? 'update' : 'create'} task. Please try again.`);
                        } finally {
                            setIsLoading(false);
                        }
                    }}>{isLoading ? (initialData?.id ? 'Updating...' : 'Creating...') : (initialData?.id ? 'Update' : 'Create')}</button>
                </div>
            </div>
        </div>
    );
};

export default CreateTask;
